/**
 * Monte Carlo Results Component
 * Displays P10/P50/P90 results from simulation
 */

import React from 'react';
import { formatCurrency } from '../utils/formatCurrency';

interface MonteCarloResultsProps {
  result: any;
}

export default function MonteCarloResults({ result }: MonteCarloResultsProps) {
  if (!result) return null;

  const { iterations, p10, p50, p90, mean, std_dev, confidence_interval_95 } = result;

  return (
    <div className="bg-gradient-to-br from-indigo-50 to-blue-50 border-2 border-indigo-200 rounded-lg p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-xl font-bold text-indigo-900">Monte Carlo Simulation Results</h4>
          <p className="text-sm text-indigo-600">{iterations?.toLocaleString()} iterations completed</p>
        </div>
        <svg className="w-12 h-12 text-indigo-400" fill="currentColor" viewBox="0 0 20 20">
          <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
        </svg>
      </div>

      {/* Percentile Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* P10 */}
        <div className="bg-white rounded-lg p-4 border-2 border-yellow-200 shadow-sm">
          <p className="text-yellow-800 text-sm font-medium mb-1">P10 (Conservative)</p>
          <p className="text-2xl font-bold text-gray-900">{formatCurrency(p10)}</p>
          <p className="text-xs text-gray-500 mt-1">10% chance loss is below this</p>
        </div>

        {/* P50 (Median) */}
        <div className="bg-white rounded-lg p-4 border-2 border-blue-200 shadow-sm">
          <p className="text-blue-800 text-sm font-medium mb-1">P50 (Median)</p>
          <p className="text-2xl font-bold text-gray-900">{formatCurrency(p50)}</p>
          <p className="text-xs text-gray-500 mt-1">50th percentile (most likely)</p>
        </div>

        {/* P90 */}
        <div className="bg-white rounded-lg p-4 border-2 border-blue-300 shadow-lg ring-2 ring-blue-400">
          <p className="text-blue-800 text-sm font-medium mb-1">P90 (Aggressive) ⭐</p>
          <p className="text-2xl font-bold text-blue-600">{formatCurrency(p90)}</p>
          <p className="text-xs text-gray-500 mt-1">90% chance loss is below this</p>
        </div>
      </div>

      {/* Statistical Summary */}
      <div className="bg-white rounded-lg p-4 space-y-3">
        <h5 className="font-medium text-gray-900">Statistical Summary</h5>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-gray-600">Mean:</span>
            <span className="ml-2 font-semibold">{formatCurrency(mean)}</span>
          </div>
          <div>
            <span className="text-gray-600">Std Dev:</span>
            <span className="ml-2 font-semibold">{formatCurrency(std_dev)}</span>
          </div>
          <div>
            <span className="text-gray-600">95% CI Low:</span>
            <span className="ml-2 font-semibold">{formatCurrency(confidence_interval_95?.lower)}</span>
          </div>
          <div>
            <span className="text-gray-600">95% CI High:</span>
            <span className="ml-2 font-semibold">{formatCurrency(confidence_interval_95?.upper)}</span>
          </div>
        </div>
      </div>

      {/* Interpretation */}
      <div className="bg-indigo-100 border border-indigo-300 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <svg className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div className="text-sm text-indigo-900">
            <p className="font-medium mb-1">Interpretation for Settlement Negotiations:</p>
            <p>
              Based on {iterations?.toLocaleString()} simulations with realistic variability, 
              there is a <strong>90% probability</strong> that the actual loss will be below <strong>{formatCurrency(p90)}</strong>. 
              This P90 figure provides a defensible upper bound for opening demands while maintaining actuarial credibility.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
